import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, Loader2 } from "lucide-react";
import InstallPrompt from "@/components/InstallPrompt";
import { BrandMark } from "@/components/brand/BrandMark";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success("Bem-vindo(a) de volta!");
      nav(u.role === "STUDENT" ? "/aluno" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#F8F3EA]">
      <InstallPrompt />
      <div className="hidden md:block md:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1758270705140-4e39219d4bba?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
          alt="Doxa School"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#111111]/60 to-[#F75A1B]/40" />
        <div className="relative z-10 h-full flex flex-col justify-end p-10 text-white">
          <div className="inline-flex items-center gap-2 mb-4">
            <BrandMark size="md" />
            <span className="font-display text-xl font-bold">Doxa School</span>
          </div>
          <h2 className="font-display text-4xl font-bold leading-tight max-w-sm">
            Acompanhe a sua turma com clareza e cuidado.
          </h2>
          <p className="mt-3 text-white/80 max-w-sm">
            Sistema interno para gestão de alunos, presença, materiais e acompanhamento formativo.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <BrandMark size="md" />
            <span className="font-display text-xl font-bold">Doxa School</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold">Entrar</h1>
          <p className="mt-2 text-[#6B655E]">Use seu e-mail e senha para acessar o sistema.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium mb-1.5">E-mail</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B655E]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="w-full pl-10 pr-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B]"
                  placeholder="voce@exemplo.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Senha</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B655E]" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full pl-10 pr-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B]"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="doxa-btn-primary w-full py-3 inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? "Entrando…" : "Entrar"}
            </button>
          </form>

          <div className="mt-8 p-4 bg-white border border-[#EBE5DB] rounded-2xl text-sm text-[#6B655E]">
            <p className="font-semibold text-[#1F1F1F] mb-1">Primeiro acesso?</p>
            Solicite à equipe Doxa um link de convite para criar sua senha e aceitar o aviso de uso de dados.
          </div>
        </div>
      </div>
    </div>
  );
}
